import React, { MouseEvent, useCallback, useMemo, useState } from 'react';
import {
  SideNavGroup,
  SideNavLink,
  SideNavLinkItemType,
} from '@amzn/aac-chrome-internal-api';
import {
  AccordionContentGroupWrapper,
  AccordionContentInnerWrapper,
  AccordionContentOuterWrapper,
  AccordionContentSubTitle,
  AccordionLink,
  AccordionLinkWrapper,
  StyledDivider as Divider,
} from '../styles';
import { useConsolidatedNavContext } from 'src/providers/ConsolidatedNavContextProvider';
import { NavMetricPublisher } from 'src/commons/utils/metrics';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import SideNavLinkFeatures from 'src/features/nav/components/ConsolidatedSideNav/SideNavLinkFeatures/SideNavLinkFeatures';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';

interface AccordionContentProps {
  id: string;
  cbSelected?: boolean;
  allToolsEnabled: boolean;
}

const AccordionContent: React.VFC<AccordionContentProps> = ({
  id,
  cbSelected = false,
  allToolsEnabled,
}) => {
  const chromeConfig = useChromeConfigContext();
  const {
    isNavOpen,
    toolbarItems,
    setSelectedNavItemId,
    setOpenModalAppName,
    activeCategoryId,
    updateSelectedNavItem,
  } = useConsolidatedNavContext();
  const toolbarItem = useMemo(
    () => toolbarItems.find((toolbarItem) => toolbarItem.id === id),
    [toolbarItems],
  );

  const [accordionHeight, setAccordionHeight] = useState<number>(0);
  const handleAccordionHeight = useCallback((node: HTMLDivElement) => {
    setAccordionHeight(node?.getBoundingClientRect().height);
  }, []);

  if (!toolbarItem) {
    return null;
  }

  const { sidebarGroups: groups } = toolbarItem;

  const onClickHandler = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, link: SideNavLink) => {
      NavMetricPublisher.publishCounterMonitor(
        `sidenavItemClicked-${link.id}`,
        1,
      );

      if (link.sidebarItemType === ('MODAL' as SideNavLinkItemType)) {
        setOpenModalAppName(link.appName ?? '');
        return;
      }

      // Do not update the currently selected app when Client Side Routing is not enabled
      if (!link.isCSREnabled) {
        return;
      }

      // Do not update the currently selected app when a link is opened in a new tab or a new window
      if (event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }

      // Updating selected nav item so that the change would get reflected in header
      updateSelectedNavItem?.(toolbarItem.id, link.id);

      // check if csr is used
      const { onRouteClick } = chromeConfig;
      if (onRouteClick) {
        event.preventDefault();
        onRouteClick({
          url: link.link.url,
          id: link.id,
        });
        setSelectedNavItemId(link.id);
        NavMetricPublisher.publishCounterMonitor(
          `sidenavItemClientSideRoutingClicked-${link.id}`,
          1,
        );
      }
    },
    [id],
  );

  const isActive: boolean = activeCategoryId === id;

  return (
    <AccordionContentOuterWrapper
      isActive={isActive}
      style={{
        maxHeight: isActive ? accordionHeight : 0,
      }}
    >
      <AccordionContentInnerWrapper ref={handleAccordionHeight}>
        {groups.map((group: SideNavGroup, index: number) => (
          <AccordionContentGroupWrapper
            key={`${index}-${group.title}`}
            direction="column"
            rowGap="xxxsmall"
          >
            {group.title && (
              <AccordionContentSubTitle>{group.title}</AccordionContentSubTitle>
            )}
            {group.links.map((link: SideNavLink) => {
              const isExternalLink: boolean =
                link.sidebarItemType === 'EXTERNAL_LINK';
              const isModal: boolean =
                link.sidebarItemType === ('MODAL' as SideNavLinkItemType);
              const linkIsSelected =
                link.isSelected ||
                (cbSelected && link.id === 'aac-menu-item-campaigns');
              return (
                <AccordionLinkWrapper
                  key={link.id}
                  isSelected={linkIsSelected}
                  isNavOpen={isNavOpen}
                  data-ccx-e2e-id={`${link.id}-accordion-link-wrapper`}
                >
                  <AccordionLink
                    isSelected={linkIsSelected}
                    href={isModal ? 'javascript:void(0)' : link.link.url}
                    target={isExternalLink ? '_blank' : '_self'}
                    data-ccx-e2e-id={link.id}
                    tabIndex={isNavOpen && isActive ? 0 : -1}
                    aria-label={link.link.label}
                    onClick={(event: React.MouseEvent<HTMLAnchorElement>) =>
                      onClickHandler(event, link)
                    }
                    {...(link.isSelected && { 'aria-current': 'page' })}
                    {...getTaktAttributes(
                      link.id,
                      chromeComponents.NAV,
                      {
                        url: link.link.url,
                      },
                      allToolsEnabled,
                    )}
                  >
                    {link.link.label}
                  </AccordionLink>
                  <SideNavLinkFeatures
                    sideNavLink={link}
                    isFocusable={isActive}
                  />
                </AccordionLinkWrapper>
              );
            })}
            {index !== groups.length - 1 && <Divider />}
          </AccordionContentGroupWrapper>
        ))}
      </AccordionContentInnerWrapper>
    </AccordionContentOuterWrapper>
  );
};

export default AccordionContent;
